import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { pathId } from '../lib/pathId';
import SEOHead from '../components/SEOHead';
import { companyLinkedinLinks, isAcceptedCompanyLinkedin, trackLinkedinClick, LINKEDIN_REFUSAL } from '../lib/linkedin';
import { 
  ArrowLeft, Building, MapPin, Globe, Users, 
  Edit2, Save, X, Calendar, Link2, ExternalLink,
  Briefcase, FileText, Clock, Building2, User, Heart, ThumbsUp,
  Linkedin, Mail, ChevronDown, ChevronRight, Loader2, Newspaper, UserPlus, CheckCircle2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FavoriteButton from '../components/FavoriteButton';
import ResearchNote from '../components/ResearchNote'; // WO-60
import { STANDARD_INDUSTRIES, INDUSTRY_CATEGORIES, getIndustryLabel } from '../constants/industries';
import { COMPANY_ROLES, ROLE_COLORS, getRoleLabel } from '../constants/roles';

// All world countries with ISO codes
const WORLD_COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (DRC)' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
];

// Helper to get country name from code
const getCountryName = (code) => {
  const country = WORLD_COUNTRIES.find(c => c.code === code);
  return country ? country.name : code;
};

// Role icons mapping
const roleIcons = {
  developer: Building,
  contractor: Briefcase,
  consultant: Users,
  supplier: Building2,
  others: Building
};

import ManageLinksDialog from '../components/dialogs/ManageLinksDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { getToken, user } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null); // 'projects' | 'tenders' | null
  const [linkedProjectDetails, setLinkedProjectDetails] = useState([]);
  const [linkedTenderDetails, setLinkedTenderDetails] = useState([]);
  const [linkedNewsDetails, setLinkedNewsDetails] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [showManageLinks, setShowManageLinks] = useState(false);

  const isAdmin = user?.role === 'admin';
  const token = getToken();
  const [savingLead, setSavingLead] = useState(false);
  const [savedAsLead, setSavedAsLead] = useState(false);

  const handleExtractAsLead = async () => {
    setSavingLead(true);
    try {
      const response = await fetch(`${API_URL}/api/opportunities/from-company/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to extract lead');
      setSavedAsLead(true);
      if (data.already_in_crm) {
        toast.info(isRTL ? 'هذه الشركة موجودة بالفعل في CRM الخاص بك' : 'This company is already in your CRM');
      } else {
        toast.success(isRTL ? 'تمت إضافة الشركة إلى CRM الخاص بك كعميل محتمل' : 'Company added to your CRM as a lead');
      }
    } catch (error) {
      console.error('Error extracting lead:', error);
      toast.error(error.message);
    } finally {
      setSavingLead(false);
    }
  };

  const translations = {
    en: {
      backToCompanies: 'Back to Companies',
      companyDetails: 'Company Details',
      editCompany: 'Edit Company',
      save: 'Save',
      cancel: 'Cancel',
      name: 'Company Name',
      country: 'Country',
      website: 'Website',
      linkedin: 'LinkedIn',
      viewOnLinkedin: 'View on LinkedIn',
      peopleAtCompany: 'People at this company',
      email: 'Email',
      roles: 'Roles',
      industries: 'Industries',
      linkedProjects: 'Linked Projects',
      linkedTenders: 'Linked Tenders',
      linkedNews: 'Linked News',
      createdAt: 'Created',
      loading: 'Loading company...',
      notFound: 'Company not found',
      saveSuccess: 'Company updated successfully',
      saveError: 'Failed to update company',
      adminOnly: 'Admin only',
      visitWebsite: 'Visit Website',
      developer: 'Developer',
      contractor: 'Contractor',
      consultant: 'Consultant',
      supplier: 'Supplier',
      projects: 'projects',
      tenders: 'tenders'
    },
    ar: {
      backToCompanies: 'العودة للشركات',
      companyDetails: 'تفاصيل الشركة',
      editCompany: 'تعديل الشركة',
      save: 'حفظ',
      cancel: 'إلغاء',
      name: 'اسم الشركة',
      country: 'الدولة',
      website: 'الموقع الإلكتروني',
      linkedin: 'لينكد إن',
      viewOnLinkedin: 'عرض على لينكدإن',
      peopleAtCompany: 'الأشخاص في هذه الشركة',
      email: 'البريد الإلكتروني',
      roles: 'الأدوار',
      industries: 'القطاعات',
      linkedProjects: 'المشاريع المرتبطة',
      linkedTenders: 'المناقصات المرتبطة',
      createdAt: 'تاريخ الإنشاء',
      loading: 'جاري تحميل الشركة...',
      notFound: 'الشركة غير موجودة',
      saveSuccess: 'تم تحديث الشركة بنجاح',
      saveError: 'فشل تحديث الشركة',
      adminOnly: 'للمسؤولين فقط',
      visitWebsite: 'زيارة الموقع',
      developer: 'مطور',
      contractor: 'مقاول',
      consultant: 'استشاري',
      supplier: 'مورد',
      projects: 'مشاريع',
      tenders: 'مناقصات',
      linkedNews: 'أخبار مرتبطة'
    }
  };

  const txt = translations[language];

  const fetchCompany = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/intelligence/companies/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(txt.notFound);
        } else if (response.status === 402) {
          const body = await response.json().catch(() => ({}));
          setError(body?.detail || body?.plan_gate?.message || txt.notFound);
        } else {
          throw new Error('Failed to fetch company');
        }
        return;
      }
      
      const data = await response.json();
      setCompany(data);
      setLiked(!!data.liked);
      setLikesCount(data.likes_count || 0);
      setEditData({
        canonical_name: data.canonical_name || '',
        website: data.website || '',
        country: data.country || '',
        linkedin: data.linkedin || '',
        email: data.email || '',
        industries: data.industries || [],
        roles: data.roles || []
      });
    } catch (err) {
      console.error('Error fetching company:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCompany();
    }
  }, [id]);

  const fetchLinkedProjects = async (projectIds) => {
    if (!projectIds?.length) { setLinkedProjectDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        projectIds.map(pid =>
          fetch(`${API_URL}/api/intelligence/projects/${pid}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedProjectDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked projects:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const fetchLinkedTenders = async (tenderIds) => {
    if (!tenderIds?.length) { setLinkedTenderDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        tenderIds.map(tid =>
          fetch(`${API_URL}/api/intelligence/tenders/${pathId(tid)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedTenderDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked tenders:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const fetchLinkedNews = async (newsIds) => {
    if (!newsIds?.length) { setLinkedNewsDetails([]); return; }
    setLoadingLinked(true);
    try {
      const results = await Promise.all(
        newsIds.map(nid =>
          fetch(`${API_URL}/api/intelligence/news/${encodeURIComponent(nid)}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }).then(r => r.ok ? r.json() : null)
        )
      );
      setLinkedNewsDetails(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching linked news:', err);
    } finally {
      setLoadingLinked(false);
    }
  };

  const toggleLinkedSection = (section, comp) => {
    if (expandedSection === section) {
      setExpandedSection(null);
      return;
    }
    setExpandedSection(section);
    if (section === 'projects') fetchLinkedProjects(comp?.linked_project_ids);
    else if (section === 'tenders') fetchLinkedTenders(comp?.linked_tender_ids);
    else if (section === 'news') fetchLinkedNews(comp?.linked_news_ids);
  };

  // Check if this company is a favorite
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!id || !token) return;
      try {
        const response = await fetch(
          `${API_URL}/api/admin/favorites/check?item_type=company&item_id=${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setIsFavorite(data.is_favorite);
        }
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };
    checkFavoriteStatus();
  }, [id, token]);

  const handleToggleLike = async () => {
    if (likeBusy || !token) return;
    setLikeBusy(true);
    const prevLiked = liked;
    const prevCount = likesCount;
    setLiked(!prevLiked);
    setLikesCount(prevCount + (prevLiked ? -1 : 1));
    try {
      const res = await fetch(`${API_URL}/api/intelligence/companies/${id}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('like failed');
      const data = await res.json();
      setLiked(data.liked);
      setLikesCount(data.likes_count);
    } catch (err) {
      setLiked(prevLiked);
      setLikesCount(prevCount);
      toast.error(language === 'ar' ? 'فشل تحديث الإعجاب' : 'Failed to update like');
    } finally {
      setLikeBusy(false);
    }
  };

  const LikeButton = () => (
    <Button
      variant="outline"
      onClick={handleToggleLike}
      disabled={likeBusy}
      className={liked ? 'border-rose-400/50 text-rose-600 dark:text-rose-400' : ''}
      title={liked ? (language === 'ar' ? 'إلغاء الإعجاب' : 'Unlike') : (language === 'ar' ? 'إعجاب' : 'Like')}
      data-testid="company-like-btn"
    >
      <ThumbsUp className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
      <span data-testid="company-like-count">{likesCount}</span>
    </Button>
  );

  const handleSave = async () => {
    if (!isAdmin) {
      toast.error(txt.adminOnly);
      return;
    }
    
    // WO-47 — only the company's LinkedIn page (or a showcase page) is accepted.
    if (!isAcceptedCompanyLinkedin(editData.linkedin)) {
      toast.error(LINKEDIN_REFUSAL[language] || LINKEDIN_REFUSAL.en);
      return;
    }
    
    setSaving(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/intelligence/companies/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const err = new Error('Failed to update company');
        if (response.status === 422 && typeof body.detail === 'string') err.userMessage = body.detail;
        throw err;
      }
      
      const result = await response.json();
      setCompany(result);
      setIsEditing(false);
      toast.success(txt.saveSuccess);
    } catch (err) {
      console.error('Error updating company:', err);
      toast.error(err.userMessage || txt.saveError);
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // WO-47 — what the LinkedIn buttons open (null: show nothing)
  const linkedinLinks = companyLinkedinLinks(company);

  if (loading) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="company-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="company-detail-error">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/companies')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {txt.backToCompanies}
        </Button>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{error || txt.notFound}</h3>
            <Button variant="outline" onClick={() => navigate('/dashboard/companies')}>
              {txt.backToCompanies}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="company-detail-page">
      <SEOHead 
        type="companies" 
        id={id} 
        fallback={{
          title: `${company.name || company.canonical_name} | Mid2Bid Companies`,
          description: `${company.industry || ''} company based in ${company.country}. Track on Mid2Bid.`
        }}
      />
      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/companies')}
          className={`flex items-center gap-2 w-fit ${isRTL ? 'flex-row-reverse' : ''}`}
          data-testid="back-to-companies-btn"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          {txt.backToCompanies}
        </Button>
        
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  {txt.cancel}
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-company-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {txt.save}
                </Button>
              </>
            ) : (
              <>
                <LikeButton />
                <FavoriteButton
                  itemType="company"
                  itemId={id}
                  isFavorite={isFavorite}
                  onToggle={(newState) => setIsFavorite(newState)}
                  token={token}
                />
                <Button
                  variant="outline"
                  onClick={handleExtractAsLead}
                  disabled={savingLead || savedAsLead}
                  data-testid="extract-lead-company-btn"
                >
                  {savingLead ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : savedAsLead ? (
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2 text-emerald-500" />
                  )}
                  {savedAsLead
                    ? (isRTL ? 'في CRM' : 'In your CRM')
                    : (isRTL ? 'استخراج كعميل محتمل' : 'Extract as Lead')}
                </Button>
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="edit-company-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  {txt.editCompany}
                </Button>
              </>
            )}
          </div>
        )}
        {!isAdmin && (
          <div className={`flex flex-wrap gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <LikeButton />
            <Button
              variant="outline"
              onClick={handleExtractAsLead}
              disabled={savingLead || savedAsLead}
              data-testid="extract-lead-company-btn"
            >
              {savingLead ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : savedAsLead ? (
                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2 text-emerald-500" />
              )}
              {savedAsLead
                ? (isRTL ? 'في CRM' : 'In your CRM')
                : (isRTL ? 'استخراج كعميل محتمل' : 'Extract as Lead')}
            </Button>
            <FavoriteButton
              itemType="company"
              itemId={id}
              isFavorite={isFavorite}
              onToggle={(newState) => setIsFavorite(newState)}
              token={token}
            />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info Card */}
        <Card className="lg:col-span-2 border-0 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Users className="w-5 h-5 text-primary" />
              {txt.companyDetails}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar and Name Header */}
            <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Avatar className="w-20 h-20 ring-4 ring-primary/20">
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-2xl">
                  {getInitials(company.canonical_name)}
                </AvatarFallback>
              </Avatar>
              <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                {/* Reference ID */}
                {company.ref_id && (
                  <Badge className="bg-primary text-primary-foreground font-mono text-sm mb-2" data-testid="company-detail-ref-id">
                    {company.ref_id}
                  </Badge>
                )}
                {isEditing ? (
                  <Input
                    value={editData.canonical_name}
                    onChange={(e) => setEditData({ ...editData, canonical_name: e.target.value })}
                    className="text-2xl font-bold h-12"
                    data-testid="edit-name-input"
                  />
                ) : (
                  <h2 className="text-2xl font-bold">{company.canonical_name}</h2>
                )}
              </div>
            </div>

            <Separator />

            {/* Company Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Country */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.country}
                </Label>
                {isEditing ? (
                  <Select
                    value={editData.country}
                    onValueChange={(value) => setEditData({ ...editData, country: value })}
                  >
                    <SelectTrigger data-testid="edit-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {WORLD_COUNTRIES.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium">
                    {company.country ? getCountryName(company.country) : '-'}
                  </p>
                )}
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Globe className="w-4 h-4" />
                  {txt.website}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.website}
                    onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                    placeholder="https://example.com"
                    data-testid="edit-website-input"
                  />
                ) : (
                  <p className="font-medium">
                    {company.website ? (
                      <a 
                        href={company.website.startsWith('http') ? company.website : `https://${company.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-2"
                      >
                        {company.website}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* LinkedIn — WO-47: two buttons instead of the raw address; nothing at
                  all when there is no company or showcase page. The page is dir="rtl"
                  in Arabic, so the buttons mirror by themselves. */}
              {(isEditing || linkedinLinks) && (
              <div className="space-y-2" data-testid="company-linkedin-section">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Linkedin className="w-4 h-4" />
                  {txt.linkedin}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.linkedin || ''}
                    onChange={(e) => setEditData({ ...editData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/..."
                    data-testid="edit-linkedin-input"
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button asChild variant="outline" size="sm" className="gap-2">
                      <a
                        href={linkedinLinks.page}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackLinkedinClick('company_linkedin_click', company, 'profile')}
                        data-testid="company-linkedin-view-btn"
                      >
                        <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                        {txt.viewOnLinkedin}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </Button>
                    {linkedinLinks.people && (
                      <Button asChild variant="outline" size="sm" className="gap-2">
                        <a
                          href={linkedinLinks.people}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackLinkedinClick('company_people_click', company, 'profile')}
                          data-testid="company-linkedin-people-btn"
                        >
                          <Users className="w-4 h-4" />
                          {txt.peopleAtCompany}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </Button>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Mail className="w-4 h-4" />
                  {txt.email}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    placeholder="contact@company.com"
                    data-testid="edit-email-input"
                  />
                ) : (
                  <p className="font-medium">
                    {company.email ? (
                      <a 
                        href={`mailto:${company.email}`}
                        className="text-primary hover:underline flex items-center gap-2"
                      >
                        {company.email}
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* Roles */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Briefcase className="w-4 h-4" />
                  {txt.roles}
                </Label>
                {isEditing ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between text-left font-normal h-auto min-h-10"
                        data-testid="company-roles-trigger"
                      >
                        <div className="flex flex-wrap gap-1 flex-1">
                          {editData.roles && editData.roles.length > 0 ? (
                            editData.roles.map(roleId => {
                              const role = COMPANY_ROLES.find(r => r.id === roleId);
                              const Icon = roleIcons[roleId] || Building;
                              return (
                                <span
                                  key={roleId}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${ROLE_COLORS[roleId] || 'bg-gray-500/10'}`}
                                >
                                  <Icon className="w-3 h-3" />
                                  {role?.label || roleId}
                                  <X
                                    className="w-3 h-3 cursor-pointer hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditData(prev => ({
                                        ...prev,
                                        roles: prev.roles.filter(r => r !== roleId)
                                      }));
                                    }}
                                  />
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground">Select roles...</span>
                          )}
                        </div>
                        <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-2" align="start">
                      <div className="space-y-1">
                        {COMPANY_ROLES.map(role => {
                          const Icon = roleIcons[role.id] || Building;
                          return (
                            <div
                              key={role.id}
                              className="flex items-center space-x-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer"
                              onClick={() => {
                                const current = editData.roles || [];
                                if (current.includes(role.id)) {
                                  setEditData(prev => ({
                                    ...prev,
                                    roles: current.filter(r => r !== role.id)
                                  }));
                                } else {
                                  setEditData(prev => ({
                                    ...prev,
                                    roles: [...current, role.id]
                                  }));
                                }
                              }}
                            >
                              <Checkbox
                                checked={(editData.roles || []).includes(role.id)}
                                data-testid={`role-checkbox-${role.id}`}
                              />
                              <Icon className="w-4 h-4" />
                              <span className="text-sm">{role.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {company.roles?.length > 0 ? (
                      company.roles.map((role, idx) => {
                        const Icon = roleIcons[role] || Building;
                        const roleData = COMPANY_ROLES.find(r => r.id === role);
                        return (
                          <Badge key={idx} className={`${ROLE_COLORS[role] || 'bg-gray-500/10'} capitalize`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {roleData?.label || role}
                          </Badge>
                        );
                      })
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                )}
              </div>

              {/* Industries */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Building2 className="w-4 h-4" />
                  {txt.industries}
                </Label>
                {isEditing ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between text-left font-normal h-auto min-h-10"
                        data-testid="company-industries-trigger"
                      >
                        <div className="flex flex-wrap gap-1 flex-1">
                          {editData.industries && editData.industries.length > 0 ? (
                            editData.industries.map(id => (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                              >
                                {getIndustryLabel(id, 'en')}
                                <X
                                  className="w-3 h-3 cursor-pointer hover:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditData(prev => ({
                                      ...prev,
                                      industries: prev.industries.filter(i => i !== id)
                                    }));
                                  }}
                                />
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground">Select industries...</span>
                          )}
                        </div>
                        <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <ScrollArea className="h-[300px]">
                        <div className="p-2">
                          {INDUSTRY_CATEGORIES.map(category => {
                            const categoryIndustries = STANDARD_INDUSTRIES.filter(
                              ind => ind.category === category.id
                            );
                            
                            if (categoryIndustries.length === 0) return null;
                            
                            return (
                              <div key={category.id} className="mb-3">
                                <div className="text-xs font-semibold text-muted-foreground px-2 py-1 bg-muted/50 rounded">
                                  {category.en}
                                </div>
                                <div className="mt-1 space-y-1">
                                  {categoryIndustries.map(industry => (
                                    <div
                                      key={industry.id}
                                      className="flex items-center space-x-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer"
                                      onClick={() => {
                                        const current = editData.industries || [];
                                        if (current.includes(industry.id)) {
                                          setEditData(prev => ({
                                            ...prev,
                                            industries: current.filter(i => i !== industry.id)
                                          }));
                                        } else {
                                          setEditData(prev => ({
                                            ...prev,
                                            industries: [...current, industry.id]
                                          }));
                                        }
                                      }}
                                    >
                                      <Checkbox
                                        checked={(editData.industries || []).includes(industry.id)}
                                        data-testid={`industry-checkbox-${industry.id}`}
                                      />
                                      <span className="text-sm">{industry.en}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {company.industries?.length > 0 ? (
                      company.industries.map((industry, idx) => (
                        <Badge key={idx} variant="outline" className="capitalize">
                          {getIndustryLabel(industry, 'en') || industry}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="w-4 h-4" />
                  {language === 'ar' ? 'الوصف' : 'Description'}
                </Label>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {company.description || '-'}
                </p>
              </div>

              {/* WO-60 — ANTHONY's latest check, with its sources */}
              <div className="md:col-span-2">
                <ResearchNote item={company} language={language} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats Card */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {language === 'ar' ? 'العناصر المرتبطة' : 'Linked Items'}
                </span>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setShowManageLinks(true)}
                    data-testid="manage-links-btn"
                  >
                    <Link2 className="w-3 h-3 mr-1" />
                    {language === 'ar' ? 'إدارة' : 'Manage'}
                  </Button>
                )}
              </div>
              {/* Linked News */}
              <div
                className="p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleLinkedSection('news', company)}
                data-testid="linked-news-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Newspaper className="w-4 h-4" />
                    <span className="text-xs">{txt.linkedNews}</span>
                  </div>
                  {expandedSection === 'news'
                    ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <p className="text-2xl font-bold text-emerald-600">{company.linked_news_ids?.length || 0}</p>
              </div>

              {/* Linked News List */}
              {expandedSection === 'news' && (
                <div className="border rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedNewsDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No news found</div>
                  ) : (
                    <div className="divide-y">
                      {linkedNewsDetails.map(item => (
                        <div
                          key={item._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/news/${item._id}`)}
                          data-testid={`linked-news-item-${item._id}`}
                        >
                          <Newspaper className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">
                              {item.title || item.title_en || 'Untitled'}
                            </p>
                            {item.published_at && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {new Date(item.published_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Linked Tenders */}
              <div
                className="p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleLinkedSection('tenders', company)}
                data-testid="linked-tenders-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FileText className="w-4 h-4" />
                    <span className="text-xs">{txt.linkedTenders}</span>
                  </div>
                  {expandedSection === 'tenders'
                    ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <p className="text-2xl font-bold text-accent">{company.linked_tender_ids?.length || 0}</p>
              </div>

              {/* Linked Tenders List */}
              {expandedSection === 'tenders' && (
                <div className="border rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedTenderDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No tenders found</div>
                  ) : (
                    <div className="divide-y">
                      {linkedTenderDetails.map(tender => (
                        <div
                          key={tender._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/tenders/${pathId(tender._id)}`)}
                          data-testid={`linked-tender-item-${tender._id}`}
                        >
                          <FileText className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">
                              {(typeof tender.title === 'object' ? (tender.title?.en || tender.title?.ar) : tender.title) || tender.canonical_name}
                            </p>
                            {tender.country && <p className="text-xs text-muted-foreground mt-0.5">{tender.country}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Linked Projects */}
              <div
                className="p-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleLinkedSection('projects', company)}
                data-testid="linked-projects-toggle"
              >
                <div className={`flex items-center justify-between gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs">{txt.linkedProjects}</span>
                  </div>
                  {expandedSection === 'projects'
                    ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <p className="text-2xl font-bold text-primary">{company.linked_project_ids?.length || 0}</p>
              </div>

              {/* Linked Projects List */}
              {expandedSection === 'projects' && (
                <div className="border rounded-lg overflow-hidden">
                  {loadingLinked ? (
                    <div className="p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : linkedProjectDetails.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No projects found</div>
                  ) : (
                    <div className="divide-y">
                      {linkedProjectDetails.map(project => (
                        <div
                          key={project._id}
                          className="flex items-start gap-2 p-2.5 hover:bg-muted/40 cursor-pointer transition-colors"
                          onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                          data-testid={`linked-project-item-${project._id}`}
                        >
                          <Building2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium leading-tight line-clamp-2">{project.canonical_name || project.title}</p>
                            {project.country && <p className="text-xs text-muted-foreground mt-0.5">{project.country}</p>}
                          </div>
                          <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timestamps Card */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-4 space-y-3">
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{language === 'ar' ? 'أنشئ بواسطة:' : 'Created by:'}</span>
                <span className="font-medium">
                  {company.created_by_name || (company.created_by === 'admin' ? 'Admin' : company.created_by) || 'Admin'}
                </span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{txt.createdAt}:</span>
                <span className="font-medium">{formatDate(company.created_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Admin Notice */}
          {!isAdmin && (
            <Card className="border-0 bg-amber-500/10 backdrop-blur-sm">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {language === 'ar' 
                    ? 'تعديل الشركة متاح للمسؤولين فقط' 
                    : 'Company editing is available for admins only'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Manage Links Dialog */}
      {company && (
        <ManageLinksDialog
          open={showManageLinks}
          onOpenChange={setShowManageLinks}
          fromType="companies"
          fromId={company._id}
          fromTitle={company.canonical_name}
          currentLinks={{
            news:      company.linked_news_ids     || [],
            tenders:   company.linked_tender_ids   || [],
            projects:  company.linked_project_ids  || [],
          }}
          token={token}
          onSuccess={() => { fetchCompany(); setExpandedSection(null); }}
        />
      )}
    </div>
  );
}
